import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { cn } from '@/lib/utils';
import { glossaryEntries } from '@/content/glossary-entries';

type Props = {
  route: RouteDefinition;
};

const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

function getEntryLetter(term: string): string {
  const firstCharacter = term.trim().charAt(0).toUpperCase();
  if (!firstCharacter) {
    return '#';
  }

  return /[A-Z]/.test(firstCharacter) ? firstCharacter : '#';
}

function getSectionId(letter: string): string {
  return letter === '#' ? 'letter-number' : `letter-${letter.toLowerCase()}`;
}

function formatTermCount(count: number): string {
  return `${count} term${count === 1 ? '' : 's'}`;
}

export function GlossaryPage({ route }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const letterByAnchor = useMemo(
    () => new Map(glossaryEntries.map((entry) => [entry.anchor, getEntryLetter(entry.term)])),
    []
  );

  const letterBySectionId = useMemo(
    () => new Map(LETTERS.map((letter) => [getSectionId(letter), letter])),
    []
  );

  const filteredEntries = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return glossaryEntries;
    }

    return glossaryEntries.filter((entry) => {
      const calculatorName = entry.calculator?.name?.toLowerCase() ?? '';

      return (
        entry.term.toLowerCase().includes(normalizedQuery) ||
        entry.definition.toLowerCase().includes(normalizedQuery) ||
        entry.example?.toLowerCase().includes(normalizedQuery) ||
        calculatorName.includes(normalizedQuery)
      );
    });
  }, [searchQuery]);

  const groupedEntries = useMemo(() => {
    const bucket = new Map<string, typeof filteredEntries>();

    filteredEntries.forEach((entry) => {
      const letter = getEntryLetter(entry.term);
      const current = bucket.get(letter) ?? [];
      current.push(entry);
      bucket.set(letter, current);
    });

    return LETTERS.filter((letter) => bucket.has(letter)).map((letter) => ({
      letter,
      entries: bucket.get(letter) ?? []
    }));
  }, [filteredEntries]);

  const availableLetters = useMemo(() => new Set(groupedEntries.map((group) => group.letter)), [groupedEntries]);

  useEffect(() => {
    const setActiveFromHash = () => {
      const normalizedHash = decodeURIComponent(window.location.hash.replace(/^#/, '').trim());
      let hashLetter: string | null = null;

      if (normalizedHash) {
        hashLetter = letterBySectionId.get(normalizedHash) ?? null;

        if (!hashLetter && normalizedHash.startsWith('letter-')) {
          const suffix = normalizedHash.replace(/^letter-/, '').toUpperCase();
          hashLetter = suffix === 'NUMBER' ? '#' : /^[A-Z]$/.test(suffix) ? suffix : null;
        }

        if (!hashLetter) {
          hashLetter = letterByAnchor.get(normalizedHash) ?? null;
        }
      }

      if (hashLetter && availableLetters.has(hashLetter)) {
        setActiveLetter(hashLetter);
        return;
      }

      setActiveLetter(groupedEntries[0]?.letter ?? null);
    };

    setActiveFromHash();
    window.addEventListener('hashchange', setActiveFromHash);

    return () => {
      window.removeEventListener('hashchange', setActiveFromHash);
    };
  }, [availableLetters, groupedEntries, letterByAnchor, letterBySectionId]);

  useEffect(() => {
    const normalizedHash = decodeURIComponent(window.location.hash.replace(/^#/, '').trim());
    if (!normalizedHash) {
      return;
    }

    const target = document.getElementById(normalizedHash);
    if (!target) {
      return;
    }

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [groupedEntries]);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Reference</p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">Financial Glossary</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Key investing and FIRE terms in plain language, with quick A-Z navigation to find what you need fast.
        </p>
      </section>

      <section className="brand-frame rounded-2xl border border-border bg-card/85 p-5 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <label className="grid gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Search terms</span>
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Try FIRE, withdrawal, or tax-advantaged"
              aria-label="Search glossary terms"
              className="w-full md:w-[26rem]"
            />
          </label>

          {searchQuery ? (
            <Button variant="outline" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {LETTERS.map((letter) => {
            const hasEntries = availableLetters.has(letter);
            const isActive = hasEntries && letter === activeLetter;

            if (!hasEntries) {
              return (
                <span
                  key={letter}
                  className="inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-border/40 bg-muted/40 px-3 text-sm font-semibold text-muted-foreground/60"
                  aria-hidden="true"
                >
                  {letter}
                </span>
              );
            }

            return (
              <a
                key={letter}
                href={`#${getSectionId(letter)}`}
                onClick={() => setActiveLetter(letter)}
                className={cn(
                  'inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-3 text-sm font-semibold transition-colors',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground shadow-soft'
                    : 'border-border bg-secondary/60 text-secondary-foreground hover:bg-accent hover:text-accent-foreground'
                )}
                aria-label={`Jump to ${letter === '#' ? 'number' : letter} terms`}
              >
                {letter}
              </a>
            );
          })}
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Showing {formatTermCount(filteredEntries.length)} of {formatTermCount(glossaryEntries.length)}.
        </p>
      </section>

      {groupedEntries.length === 0 ? (
        <Card className="brand-frame border-border/80 bg-card/92">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No glossary terms matched your search. Try a broader keyword.
          </CardContent>
        </Card>
      ) : (
        <section className="grid gap-4">
          {groupedEntries.map((group) => (
            <Card
              key={group.letter}
              id={getSectionId(group.letter)}
              className="brand-frame scroll-mt-24 border-border/80 bg-card/92"
            >
              <CardHeader className="pb-3">
                <div className="flex items-end justify-between gap-3">
                  <CardTitle className="font-display text-3xl leading-none">{group.letter}</CardTitle>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {formatTermCount(group.entries.length)}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="grid gap-3">
                {group.entries.map((entry) => (
                  <article
                    key={entry.anchor}
                    id={entry.anchor}
                    className="scroll-mt-24 rounded-xl border border-border/70 bg-background/65 p-4"
                  >
                    <h3 className="font-display text-2xl leading-tight text-foreground">{entry.term}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{entry.definition}</p>
                    {entry.example ? (
                      <p className="mt-3 rounded-lg border border-border/60 bg-secondary/45 px-3 py-2 text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">Example:</span> {entry.example}
                      </p>
                    ) : null}
                    {entry.calculator ? (
                      <p className="mt-3 text-sm">
                        <Link
                          to={entry.calculator.path}
                          className="font-semibold text-primary underline decoration-primary/45 underline-offset-4 hover:text-foreground hover:decoration-foreground"
                        >
                          Try the {entry.calculator.name}
                        </Link>
                      </p>
                    ) : null}
                  </article>
                ))}
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
